<template>
  <div v-if="isVisible">
    <p class="subtitle mt-3">{{ title }}</p>
    <div>
      <div class="row mb-2">
        <div
          v-for="(field, index) in fields"
          :key="index"
          class="col-12 mb-1"
        >
          <el-form-item :label="field.label.includes('.') ? $t(field.label) : field.label" label-position="top">
            <div class="file-upload">
              <el-tooltip
                effect="dark"
                :content="$t('components.order-form.chooseFilesTooltip')"
                placement="top"
                :disabled="userStore.isAuthenticated && !disabled"
                popper-class="large-tooltip"
              >
              
                <label
                  :for="'file-upload-' + index"
                  :class="['custom-upload-btn', { 'disabled-btn': !userStore.isAuthenticated || disabled }]
                "
                >
                  <el-icon><Upload /></el-icon>
                  {{ $t('views.configurator.chooseFiles') }}
                </label>
              </el-tooltip>

              <input
                :disabled="!userStore.isAuthenticated || disabled"
                :key="inputKey"
                :id="'file-upload-' + index"
                type="file"
                :accept="field.accept"
                :multiple="field.multiple"
                @change="(e) => onFileChange(e, index)"
              />

              <div class="file-list" v-if="selectedFilesMap[index]?.length">
                <span
                  v-for="(file, fileIndex) in selectedFilesMap[index]"
                  :key="fileIndex"
                  class="file-item"
                >
                  {{ file.name }}
                  <div class="file-progress-wrapper">
                    <el-tooltip
                      v-if="fileProgressMap[file.name].status === 'warning'"
                      :content="fileProgressMap[file.name].errorMessage"
                      placement="top"
                    >
                      <el-progress
                        :percentage="fileProgressMap[file.name].percentage"
                        status="warning"
                      />
                    </el-tooltip>
                    <el-progress
                      v-else
                      :percentage="fileProgressMap[file.name].percentage"
                      :status="fileProgressMap[file.name].percentage === 100 ? 'success' : ''"
                    />
                    <button
                      type="button"
                      class="remove-btn"
                      @click.stop.prevent="
                        fileProgressMap[file.name].percentage < 100
                          ? abortUpload(file.name, index, $event)
                          : removeFile(file, index, $event)
                      "
                    >
                      <el-icon>
                        <component
                          :is="
                            fileProgressMap[file.name].percentage < 100
                              ? 'CloseBold'
                              : 'Remove'
                          "
                        />
                      </el-icon>
                    </button>
                  </div>
                </span>
              </div>
            </div>
          </el-form-item>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { useUserStore } from '@/stores/userStore';
import { ElMessage } from 'element-plus';
import { BlobUploadCategory } from '@/types/enum/blobUploadCategory';
import { AzureBlobUploadService } from '@/services/api/azureBlobUploadService';
import { updateOrderFiles } from '@/services/api/orderService';
import { Order } from '@/types/api/Business/Order';

interface FileInputOption {
  label: string;
  accept: string;
  class: string;
  multiple: boolean;
}

@Component
export default class OrderFormUploadData extends Vue {
  @Prop() order!: Record<string, any>;
  @Prop({ type: String, required: true }) title!: string;
  @Prop({ type: Array, required: true }) fields!: FileInputOption[];
  @Prop({ type: Boolean, default: true }) isVisible!: boolean;
  @Prop({ type: Boolean, default: false }) disabled!: boolean;
  @Prop({ type: Boolean, default: false }) isExistingOrder?: boolean;

  selectedFilesMap: Record<number, File[]> = {};
  fileProgressMap: Record<
    string,
    { percentage: number; status: '' | 'success' | 'exception' | 'warning'; errorMessage?: string }
  > = {};
  uploadControllers: Record<string, AbortController> = {};
  fileUrlMap: Record<string, string> = {};
  inputKey = 0;
  userStore = useUserStore();
  isUploadInProgress = false;
  allScanUrls: string[] = [];
  allImageUrls: string[] = [];
  editedOrder: Partial<Order> = {};

  azureService = new AzureBlobUploadService();

  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.editedOrder = { ...this.order };
  }

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.isUploadInProgress) {
      const msg = 'You have ongoing file uploads. If you leave, they will be interrupted.';
      event.returnValue = msg;
      return msg;
    }
  }

  async onFileChange(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    if (!input?.files?.length) return;

    const SCAN_EXTS = new Set(['stl', 'obj', 'fbx', 'mtl', 'ply']);
    const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg']);

    const extOf = (f: File) => f.name.split('.').pop()?.toLowerCase() || '';
    const byExt = (set: Set<string>) => (f: File) => set.has(extOf(f));


    const sanitizedFiles = Array.from(input.files).map(f => {
      const cleanName = this.sanitizeFileNameSimple(f.name);
      return new File([f], cleanName, { type: f.type, lastModified: f.lastModified });
    });

    const filteredFiles = sanitizedFiles;
    if (!filteredFiles.length) return;


    const existing = this.selectedFilesMap[index] || [];
    const uniqueFiles = [
      ...existing,
      ...filteredFiles.filter((f) => !existing.some((e: File) => e.name === f.name)),
    ];
    this.selectedFilesMap = { ...this.selectedFilesMap, [index]: uniqueFiles };

    
    filteredFiles.forEach((f) => {
      this.fileProgressMap[f.name] ??= { percentage: 0, status: '' };
      this.uploadControllers[f.name] ??= new AbortController();
    });

    this.inputKey++;

    const scanFiles = filteredFiles.filter(byExt(SCAN_EXTS));
    const imageFiles = filteredFiles.filter(byExt(IMAGE_EXTS));

    const onProgress = (fileName: string, percent: number) => {
      if (this.uploadControllers[fileName]?.signal.aborted) return;
      this.fileProgressMap[fileName] ??= { percentage: 0, status: '' };
      this.fileProgressMap[fileName].percentage = percent;
      if (percent === 100) this.fileProgressMap[fileName].status = 'success';
    };

    try {
      this.isUploadInProgress = true;
      this.$emit('progress', true);

      if (this.isExistingOrder) {
        console.log("Updating Order Files");
        const orderId = this.order.orderId;
        const orderNumber = this.order.orderNumber;

        await this.uploadBatch(scanFiles, true, orderId, orderNumber, onProgress);
        await this.uploadBatch(imageFiles, false, orderId, orderNumber, onProgress);

        this.editedOrder.scanFiles = [...this.editedOrder.scanFiles || [], ...this.allScanUrls];
        this.editedOrder.imageFiles = [...this.editedOrder.imageFiles || [], ...this.allImageUrls];
      } else {
        const draftOrder = JSON.parse(localStorage.getItem("draftOrder") ?? "null");

        await this.uploadBatch(scanFiles, true, draftOrder?.orderId, draftOrder.orderNumber, onProgress);
        await this.uploadBatch(imageFiles, false, draftOrder?.orderId, draftOrder.orderNumber, onProgress);
      
        this.order.scanFiles = [...this.allScanUrls];
        this.order.imageFiles = [...this.allImageUrls];
      }
    } catch (e) {
      console.error('Unexpected upload error', e);
    } finally {
      this.isUploadInProgress = false;
      this.$emit('progress', false);
      this.$emit('editedOrderFiles', this.editedOrder);
    }
  }

  async uploadBatch(files: File[], isScan: boolean, orderId: number, orderNumber: string, onProgress: (name: string, p: number) => void) {
    if (!files.length) return;

    const results = await Promise.allSettled(
      files.map((file) => this.uploadSingle(file, orderId, orderNumber, onProgress))
    );

    for (const r of results) {
      if (r.status === 'fulfilled') {
        const { file, url } = r.value;
        this.fileUrlMap[file.name] = url;

        if (isScan) this.allScanUrls.push(url);
        else this.allImageUrls.push(url);

        this.fileProgressMap[file.name] ??= { percentage: 0, status: '' };
        this.fileProgressMap[file.name].percentage = 100;
        this.fileProgressMap[file.name].status = 'success';
      } else {
        const reason: any = r.reason;
        const file: File | undefined = reason?.file;
        const name = file?.name ?? reason?.fileName ?? 'unknown';
        const aborted = reason?.name === 'AbortError' || reason?.code === 'ERR_CANCELED';

        this.fileProgressMap[name] ??= { percentage: 0, status: '' };
        this.fileProgressMap[name].status = 'warning';
        this.fileProgressMap[name].errorMessage = aborted
          ? (this as any).$t('components.order-form.uploadCanceled', { filename: name })
          : (reason?.message || 'Upload failed');
      }
    }
  }

  async uploadSingle(file: File, orderId: number, orderNumber: string, onProgress: (name: string, p: number) => void) {
    const ctrl = this.uploadControllers[file.name];
    const urls: string[] = await this.azureService.uploadFiles({
      type: BlobUploadCategory.Order,
      orderId: orderId,
      orderNumber: orderNumber,
      files: [file],
      signalMap: { [file.name]: ctrl },
      onProgress,
    });
    return { file, url: urls[0] as string };
  }



  async abortUpload(fileName: string, index: number, event?: Event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const ctrl = this.uploadControllers[fileName];
    if (ctrl && !ctrl.signal.aborted) ctrl.abort();
    delete this.uploadControllers[fileName];

    ElMessage.warning(
      (this as any).$t('components.order-form.uploadCanceled', { filename: fileName })
    );

    await this.removeFile({ name: fileName } as File, index);
  }

  findUrlByFileName(urls: string[], name: string) {
    for (const u of urls) {
      try {
        const pathname = new URL(u).pathname;
        const last = pathname.split('/').pop() || '';
        const decoded = decodeURIComponent(last);
        if (decoded === name) return u;
      } catch { /* ignore bad URLs */ }
    }
    return undefined;
  }

  async removeFile(file: File, index: number, event?: Event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    this.selectedFilesMap[index] = (this.selectedFilesMap[index] || [])
      .filter(f => f.name !== file.name);

    const fileUrl = this.fileUrlMap[file.name];

    if (fileUrl) {
      this.azureService.deleteFileByUrl(BlobUploadCategory.Order, fileUrl).catch(() => {});
    }

    if (fileUrl) {
      this.allScanUrls = this.allScanUrls.filter(u => u !== fileUrl);
      this.allImageUrls = this.allImageUrls.filter(u => u !== fileUrl);
    }
    delete this.fileUrlMap[file.name];

    if (this.isExistingOrder) {
      this.editedOrder.scanFiles = [...this.editedOrder.scanFiles || [], ...this.allScanUrls];
      this.editedOrder.imageFiles = [...this.editedOrder.imageFiles || [], ...this.allImageUrls];

      this.$emit('editedOrderFiles', this.editedOrder);
    } else {
      this.order.scanFiles = [...this.allScanUrls];
      this.order.imageFiles = [...this.allImageUrls];
    }
  }



  _fallbackCounter = 0;

  nextFallbackBase(): string {
   this._fallbackCounter += 1;
    return `file${this._fallbackCounter}`;
  }

  isAsciiAlnumOrSep(ch: string): { allowed: boolean; isSep: boolean } {
    const code = ch.charCodeAt(0);
    const isSep = ch === "." || ch === "_" || ch === "-";
    const isDigit = code >= 48 && code <= 57;
    const isUpper = code >= 65 && code <= 90;
    const isLower = code >= 97 && code <= 122;
    return { allowed: isSep || isDigit || isUpper || isLower, isSep };
  }

  sanitizeFileNameSimple(original: string): string {
    const MAX_LEN = 4096;
    original = original.slice(0, MAX_LEN);

    const lastDot = original.lastIndexOf(".");
    const hasExt = lastDot > 0 && lastDot < original.length - 1;
    const base = hasExt ? original.slice(0, lastDot) : original;
    const ext  = hasExt ? original.slice(lastDot + 1) : "";

    let s = base.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    s = s.replace(/\s+/g, "-");

    let out = "";
    let prevWasSep = false;
    for (const ch of s) {
      const { allowed, isSep } = this.isAsciiAlnumOrSep(ch);
      if (!allowed) continue;
      if (isSep) {
        if (prevWasSep) continue;
        out += "-";
        prevWasSep = true;
        continue;
      }
      out += ch;
      prevWasSep = false;
    }

    if (out.startsWith("-")) out = out.slice(1);
    if (out.endsWith("-"))  out = out.slice(0, -1);

    const safeBase = out || this.nextFallbackBase();
    const cleanExt = ext.toLowerCase().replace(/[^a-z0-9]/g, "");
    return cleanExt ? `${safeBase}.${cleanExt}` : safeBase;
  }

}
</script>

<style scoped>
input[type='file'] {
  display: none;
}

.file-upload {
  flex-direction: column;
  width: 100%;
}

.custom-upload-btn {
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: #fff;
  color: #1d1e19;
  padding: 0.5rem 2rem 0.5rem 2rem;
  border-style: solid;
  border-width: 0.1rem;
  border-radius: 0.3rem;
  border-color: var(--color-primary);
  cursor: pointer;
  transition: background-color 0.3s;
}

.custom-upload-btn:hover {
  background-color: var(--color-primary);
  color: var(--primary-text);
}

.custom-upload-btn.disabled-btn {
  background-color: #ddd;
  color: #888;
  border-color: var(--color-primary);
  opacity: 0.6;
}

/* File List */
.file-list {
  margin-top: 8px;
}

.file-list span {
  display: block;
  font-size: 14px;
}

.subtitle {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 5px !important;
}

.light {
  margin-top: 0.5rem;
  font-weight: normal;
  color: #1d1e19;
}
.file-item {
  display: flex;
  align-items: center;
  gap: 3px;
  width: 100%;
}

.file-progress-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
}

.el-progress {
  flex-grow: 1;
  min-width: 100px;
}

.remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  margin-left: -30px;
  flex-shrink: 0;
}

.remove-btn:hover {
  color: red;
}
</style>
